import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import theme from './theme';
import './styles/ios-pwa-fixes.css';

// Pages
import Login from './pages/Login/Login';
import ForgotPassword from './pages/Login/ForgotPassword';
import TwoFactorVerification from './pages/Login/TwoFactorVerification';
import FirstTimePasswordChange from './pages/Login/FirstTimePasswordChange';
import Timecard from './pages/Timecard/Timecard';
import Directory from './pages/Directory/Directory';
import Resources from './pages/Resources/Resources';
import Reporting from './pages/Reporting/Reporting';
import HoursApproval from './pages/HoursApproval/HoursApproval';
import Customers from './pages/Administration/Customers';
import Projects from './pages/Administration/Projects';
import CreateTimecards from './pages/Administration/CreateTimecards';
import EditTimecards from './pages/Administration/EditTimecards';
import Users from './pages/System/Users';
import Teams from './pages/System/Teams';
import Settings from './pages/System/Settings';

// Components
import Layout from './components/Layout/Layout';
import SimplePWAInstall from './components/PWA/SimplePWAInstall';

const PrivateRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const DefaultRoute = () => {
  const { user } = useAuth();
  
  if (user.role === 'admin' || user.role === 'accountant') {
    return <Navigate to="/reporting" />;
  }
  return <Navigate to="/timecard" />;
};

const App = () => {
  // Disable scroll restoration to prevent iOS Safari PWA from preserving scroll position
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-2fa" element={<TwoFactorVerification />} />
            <Route path="/change-password" element={<FirstTimePasswordChange />} />
            
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<DefaultRoute />} />
              
              <Route
                path="timecard"
                element={
                  <PrivateRoute roles={['user', 'team_lead']}>
                    <Timecard />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="reporting"
                element={
                  <PrivateRoute roles={['user', 'team_lead', 'admin', 'accountant']}>
                    <Reporting />
                  </PrivateRoute>
                }
              />

              <Route
                path="directory"
                element={
                  <PrivateRoute>
                    <Directory />
                  </PrivateRoute>
                }
              />

              <Route
                path="resources"
                element={
                  <PrivateRoute>
                    <Resources />
                  </PrivateRoute>
                }
              />

              <Route
                path="hours-approval"
                element={
                  <PrivateRoute roles={['team_lead']}>
                    <HoursApproval />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="customers"
                element={
                  <PrivateRoute roles={['admin', 'team_lead']}>
                    <Customers />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="projects"
                element={
                  <PrivateRoute roles={['admin', 'team_lead']}>
                    <Projects />
                  </PrivateRoute>
                }
              />

              <Route
                path="create-timecards"
                element={
                  <PrivateRoute roles={['admin', 'team_lead']}>
                    <CreateTimecards />
                  </PrivateRoute>
                }
              />

              <Route
                path="edit-timecards"
                element={
                  <PrivateRoute roles={['admin', 'team_lead']}>
                    <EditTimecards />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="users"
                element={
                  <PrivateRoute roles={['admin', 'team_lead']}>
                    <Users />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="teams"
                element={
                  <PrivateRoute roles={['admin', 'team_lead']}>
                    <Teams />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="settings"
                element={
                  <PrivateRoute roles={['admin', 'team_lead', 'user']}>
                    <Settings />
                  </PrivateRoute>
                }
              />
            </Route>
          </Routes>
        </Router>
        {/* Simple PWA Install - basic functionality only */}
        <SimplePWAInstall />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
